import { ResourceDriver } from '../../Utility/Resource/ResourceManager';
import System from '../System';
import GameObject from '../../GameObject/GameObject';
import Overlay from '../../Prototype/UI/Overlay';
import HidePopupTask from './Task/HidePopupTask';
import ShowPopupTask from './Task/ShowPopupTask';
import { StartAnimation } from '../Animation/AnimationHelper';
import Game from '../../Game';
import Text from '../../Prototype/Text';
import Point from '../../Utility/Point';
import BaseUIHandler from './BaseUIHandler';
import BaseMenu, { MenuState } from './Menu/BaseMenu';
import { AUDIO_PACKS } from '../Audio';
import { ContextType } from '../../State/Game/ContextType';
import { IGameState } from '../../State/Game/IGameState';
import Container from '../../Prototype/Container';
import { AsyncActionStatus } from 'State/AsyncAction';
import Renderable from 'Component/Renderable';
import PIXIRenderer from 'System/Render/PIXIRenderer';

export enum UIEvent {
    TOGGLE_INTERACTIVITY = 'toggleInteractivity',
    RESTORE_INTERACTIVITY = 'restoreInteractivity',
    DISABLE_INTERACTIVITY = 'disableInteractivity',
    HIDE_HUD = 'hideHUD',
}

export default class BaseUISystem extends System {
    /**
     * Overlay behind current popup.
     */
    public overlayPopup: Overlay;

    /**
     * Popup layers.
     */
    public popupLayer: GameObject;

    /**
     * Overlay to cover entire UI (and rest of game).
     */
    public UIOverlay: GameObject;

    /**
     * Whether the UI overlay is currently shown.
     */
    public UIOverlayActive: boolean;

    public handlers: BaseUIHandler[];

    public currentPopup: BaseMenu;
    public currentPopupID: string | null = null;

    // tslint:disable-next-line:no-empty
    public init(): void {}

    public async preload(): Promise<void> {
        // Load all required ui audios from audio sprite sheet.
        // please take a look at README on resources/audio directory.
        Game.resource.add(AUDIO_PACKS.uiSystem, undefined, ResourceDriver.SECONDARY);

        // Await preloading of all required handlers.
        await Promise.all([...this.handlers.values()].map(handler => handler.preload()));
    }

    public onStart(): void {
        // Create visible handlers.
        this.handlers.filter(handler => handler.alwaysVisible).forEach(handler => handler.create());

        this.popupLayer = GameObject.Instantiate(Container, {
            id: 'PopupLayer',
        });

        this.overlayPopup = GameObject.Instantiate(Overlay, {
            id: 'UISystemPopupOverlay',
            preventInteraction: true,
        });

        this.popupLayer.addChild(this.overlayPopup);
        this.scene.addGameObject(this.popupLayer);

        this.UIOverlay = GameObject.Instantiate(Overlay, {
            id: 'UISystemOverlay',
        });

        const loadingText = GameObject.Instantiate(Text, {
            name: 'loading_text',
            text: Game.locale.getText('Loading'),
            style: Game.style.getStyle('loading.text'),
            relativePosition: new Point(0.5, 0.5),
            anchor: new Point(0.5, 0.5),
        });
        this.UIOverlay.addChild(loadingText);
        this.scene.addGameObject(this.UIOverlay);

        PIXIRenderer.rootContainer.on('resized', this.resize);
    }

    public onDestroy(): void {
        PIXIRenderer.rootContainer.off('resized', this.resize);
        this.handlers.forEach(handler => handler.destroy());
    }

    public handleStateChanged(state: IGameState): void {
        // Each handler can implement its own state change handler.
        this.handlers.filter(handler => handler.active).forEach(handler => handler.handleStateChanged(state));

        this.checkPopup(state);
    }

    private checkPopup(state: IGameState) {
        const id = state.UIState.id;
        // Close any open pop-ups before showing new ones.
        if (this.currentPopup && (id === null || id !== this.currentPopupID)) {
            const handler = this.currentPopup;
            this.currentPopup = null;
            this.currentPopupID = null;
            this.hidePopup(handler, state);
        }

        // Show a popup if an ID is given and if there is a current popup the ID must be different.
        if (id !== null && (!this.currentPopup || id !== this.currentPopupID)) {
            const handler = this.handlers.find(e => e.name === state.UIState.popupID);
            if (handler && handler instanceof BaseMenu) {
                this.currentPopup = handler;
                this.currentPopupID = id;
                this.showPopup(handler, state);
            }
        }
        const isGBCConnected =
            state.gbcStatus === AsyncActionStatus.COMPLETE &&
            state.dataConflict.status !== AsyncActionStatus.IN_PROGRESS;

        // Handle UI context overlay.
        if (this.UIOverlayActive && (state.context.current === ContextType.DEFAULT || !isGBCConnected)) {
            this.UIOverlayActive = false;
            // Context has returned to 'default' - hide the overlay.
            if (this.UIOverlay) StartAnimation(this.UIOverlay, 'OverlayFadeOut');
        } else if (state.context.current !== ContextType.DEFAULT && isGBCConnected) {
            this.UIOverlayActive = true;
            if (this.UIOverlay) StartAnimation(this.UIOverlay, 'OverlayFadeIn');
        }
    }

    public hidePopup(handler: BaseMenu, state: IGameState) {
        handler.currentState = MenuState.CLOSED;
        this.taskQueue.queue(new HidePopupTask(handler, state.UIState));
        this.taskQueue.start(this);
    }

    public showPopup(handler: BaseMenu, state: IGameState) {
        handler.currentState = MenuState.OPEN;
        this.taskQueue.queue(new ShowPopupTask(handler, state.UIState));
        this.taskQueue.start(this);
    }

    public onLateUpdate(): void {}

    public onStop(): void {}

    public onUpdate(): void {}

    protected resize(screen: { CANVAS_WIDTH: number; CANVAS_HEIGHT: number }): void {
        if (this.overlayPopup) {
            this.overlayPopup.container.resize(screen.CANVAS_WIDTH, screen.CANVAS_HEIGHT);
        }
    }
}
